import { XMarkIcon } from '@heroicons/react/24/outline';
import { useCallback, useState } from 'react';

import { Badge, type BadgeVariant } from '@/components/core/Badge';
import { Icon } from '@/components/core/Icon';
import { Input } from '@/components/core/Input';
import { Loader } from '@/components/core/Loader';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { hasSettingFeature } from '@/services/angularjs-bridge';
import { queryClient } from '@/services/query-client';

import { isUserDefinedFilter, useFiltersService } from '../../hooks/useFiltersService';
import { BrowseActiveFilter, FILTER_TYPE, ItemsListMeta } from '../../types';
import {
    BulkSearch,
    BulkSearchDialogState,
    BulkSearchDropdown,
} from './BulkSearch';
import { useBulkSearchLoading } from './BulkSearch/useBulkSearchLoading';
import { FilterBuilder } from './FilterBuilder';
import { ItemsSearchFilter, ItemsSearchFilterQuery } from './FilterBuilder/FilterBuilder.types';
import { resolveFilterKeywordForEdit } from './FilterBuilder/filterGroups';
import { useDeleteFilterProfile } from './FilterBuilder/filterProfiles.queries';
import { FilterDropdown } from './FilterDropdown/FilterDropdown';
import { decodeItemQuery } from './FilterDropdown/filterQuery';
import { formatFilterBadgeLabel } from './formatFilterBadgeLabel';
import { FoundItemsLabel } from './FoundItemsLabel';
import { useSearchKeyword } from './useSearchKeyword';

const ERROR_CODE_SEARCH_QUERY_INVALID = 301;

/** Angular `search-filter.tpl.html` uses `validation-warning` for code 301; OpenSearch internals are an error. */
export type SearchValidationSeverity = 'warning' | 'error';

const FILTER_BADGE_VARIANT: Record<string, BadgeVariant> = {
    USER_DEFINED: 'info',
    DATA_MODEL_DEFINED: 'info',
    PUBLISHED_TO: 'success',
    COMPLIANT_ITEMS: 'success',
    NON_COMPLIANT_ITEMS: 'error',
    VALIDATION_ERRORS: 'error',
    REVIEW_ERRORS: 'error',
    VALIDATION_WARNINGS: 'warning',
    REVIEW_WARNINGS: 'warning',
    BULK_SUBSCRIPTION: 'neutral',
    CATEGORY: 'neutral',
    BULK_SEARCH: 'info',
};

/** Close icon uses the same semantic scale as the Badge (e.g. error-darker on error-lighter). */
const CLOSE_ICON_BY_VARIANT: Record<
    BadgeVariant,
    { variant: 'info' | 'success' | 'destructive' | 'warning' | 'dark'; colorScheme: 'normal' | 'darker' }
> = {
    info: { variant: 'info', colorScheme: 'darker' },
    success: { variant: 'success', colorScheme: 'darker' },
    error: { variant: 'destructive', colorScheme: 'darker' },
    warning: { variant: 'warning', colorScheme: 'darker' },
    neutral: { variant: 'dark', colorScheme: 'normal' },
};

const VALIDATION_MESSAGE_CLASS: Record<SearchValidationSeverity, string> = {
    warning: 'validation validation-warning tw-bg-warning-lighter tw-text-warning-dark',
    error: 'validation validation-error tw-bg-error-lighter tw-text-error-dark',
};

export interface SearchFilterBarProps {
    keyword?: string;
    onKeywordChange: (keyword: string) => void;
    activeFilters?: Record<string, BrowseActiveFilter>;
    onSetActiveFilter?: (type: string, filter: BrowseActiveFilter) => void;
    onRemoveFilter?: (filter: BrowseActiveFilter) => void;
    onApplyBulkSearch?: (bulkSearchId: string) => void;
    /** Override `BULK_SEARCH` feature flag (Storybook). */
    showBulkSearch?: boolean;
    bulkSearchDialogState?: BulkSearchDialogState | null;
    onBulkSearchDialogStateConsumed?: () => void;
    itemsMeta?: ItemsListMeta | null;
    countingItems?: boolean;
    onRefreshCount?: () => void;
    /** The exact total replaced the count limit. */
    countRecalculated?: boolean;
    errorCode?: number;
    errorMessage?: string;
    /** `warning` = invalid query (Angular default). `error` = internal search failure. */
    errorSeverity?: SearchValidationSeverity;
}

/**
 * Browse search bar: Bulk Search (feature-flagged), Filters dropdown
 * (LAX-10134), keyword input, active-filter badges, and the "N items found"
 * label from `search-filter.tpl.html`.
 */
export function SearchFilterBar({
    keyword = '',
    onKeywordChange,
    activeFilters = {},
    onSetActiveFilter,
    onRemoveFilter,
    onApplyBulkSearch,
    showBulkSearch = hasSettingFeature('BULK_SEARCH'),
    bulkSearchDialogState = null,
    onBulkSearchDialogStateConsumed,
    itemsMeta,
    countingItems = false,
    onRefreshCount,
    countRecalculated = false,
    errorCode,
    errorMessage,
    errorSeverity = 'warning',
}: SearchFilterBarProps) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { data: savedFilters = [] } = useFiltersService();
    const deleteFilterProfile = useDeleteFilterProfile();
    const [isFilterBuilderOpen, setIsFilterBuilderOpen] = useState(false);
    const [editingFilter, setEditingFilter] = useState<ItemsSearchFilter | null>(null);
    const bulkSearchId = activeFilters[FILTER_TYPE.BULK_SEARCH]?.itemsQuery?.bulkSearchId;
    const bulkSearchLoading = useBulkSearchLoading(bulkSearchId);

    const handleDebouncedKeywordChange = useCallback(
        (value: string) => {
            if (value !== keyword) {
                onKeywordChange(value);
            }
        },
        [keyword, onKeywordChange],
    );

    const handleApplyBulkSearch = useCallback(
        (bulkSearch: BulkSearch) => {
            onApplyBulkSearch?.(String(bulkSearch.id));
        },
        [onApplyBulkSearch],
    );

    const handleEditFilter = useCallback(() => {
        const selected = activeFilters[FILTER_TYPE.USER_DEFINED];
        if (!selected?.name || selected.readOnly) {
            return;
        }
        const stored = savedFilters
            .filter(isUserDefinedFilter)
            .find((filter) => (
                filter.name === selected.name
                && (selected.id == null || filter.id == null || filter.id === selected.id)
            ));
        const rawKeyword = resolveFilterKeywordForEdit(
            stored?.itemsQuery?.keyword,
            selected.itemsQuery?.keyword,
            keyword,
        );
        setEditingFilter({
            name: selected.name,
            isPublic: stored?.isPublic ?? selected.isPublic,
            id: stored?.id ?? selected.id,
            itemsQuery: rawKeyword ? { keyword: rawKeyword } : undefined,
        });
        setIsFilterBuilderOpen(true);
    }, [activeFilters, keyword, savedFilters]);

    const handleCreateFilter = useCallback(() => {
        const selected = activeFilters[FILTER_TYPE.USER_DEFINED];
        if (selected?.name && !selected.readOnly) {
            handleEditFilter();
            return;
        }
        setEditingFilter(null);
        setIsFilterBuilderOpen(true);
    }, [activeFilters, handleEditFilter]);

    const handleDeleteFilter = useCallback(async () => {
        const selected = activeFilters[FILTER_TYPE.USER_DEFINED];
        if (!selected?.name || selected.readOnly) {
            return;
        }
        try {
            await deleteFilterProfile.mutateAsync({ identifier: selected.name });
            await queryClient.invalidateQueries({ queryKey: ['browse', 'filters'] });
            toast.success(t('ITEMS_SEARCH_FILTER.DELETE_SUCCESS_MESSAGE'));
            onRemoveFilter?.(selected);
        } catch {
            toast.error(t('ITEMS_SEARCH_FILTER.DELETE_ERROR_MESSAGE'));
        }
    }, [activeFilters, deleteFilterProfile, onRemoveFilter, t, toast]);

    const handleFilterQueryChange = useCallback(
        (itemsQuery: ItemsSearchFilterQuery) => {
            const keyword = itemsQuery.keyword ? decodeItemQuery(itemsQuery.keyword) : '';
            onKeywordChange(keyword);
            const selected = activeFilters[FILTER_TYPE.USER_DEFINED];
            if (selected?.name && keyword) {
                onSetActiveFilter?.(FILTER_TYPE.USER_DEFINED, {
                    ...selected,
                    itemsQuery: { ...selected.itemsQuery, keyword },
                });
            }
        },
        [activeFilters, onKeywordChange, onSetActiveFilter],
    );

    const handleFilterSaved = useCallback(
        (saved: ItemsSearchFilter) => {
            const keyword = saved.itemsQuery?.keyword
                ? decodeItemQuery(saved.itemsQuery.keyword)
                : '';
            onSetActiveFilter?.(FILTER_TYPE.USER_DEFINED, {
                name: saved.name,
                type: FILTER_TYPE.USER_DEFINED,
                id: saved.id ?? saved.identifier,
                readOnly: saved.readOnly,
                isPublic: saved.isPublic,
                itemsQuery: keyword ? { keyword } : saved.itemsQuery,
            });
        },
        [onSetActiveFilter],
    );

    const { inputValue, setInputValue } = useSearchKeyword({
        keyword,
        onDebouncedKeywordChange: handleDebouncedKeywordChange,
    });

    const selectedCategory = activeFilters[FILTER_TYPE.CATEGORY]?.itemsQuery?.category ?? null;
    const visibleFilters = Object.values(activeFilters).filter((filter) => filter.name);

    return (
        <div className="tw-mb-2">
            <div className="tw-flex tw-items-stretch tw-gap-1">
                {showBulkSearch && (
                    <BulkSearchDropdown
                        onApplyBulkSearch={handleApplyBulkSearch}
                        dialogState={bulkSearchDialogState}
                        onDialogStateConsumed={onBulkSearchDialogStateConsumed}
                        category={selectedCategory}
                    />
                )}
                <div className="tw-flex tw-flex-1 tw-items-stretch tw-border tw-border-solid tw-border-primary tw-rounded tw-bg-fill-primary">
                    <FilterDropdown
                        activeFilters={activeFilters}
                        onSelectFilter={onSetActiveFilter ?? (() => undefined)}
                        onCreateFilter={handleCreateFilter}
                        onEditFilter={handleEditFilter}
                        onDeleteFilter={handleDeleteFilter}
                    />
                    <div className="tw-flex-1 tw-min-h-9 tw-flex tw-flex-wrap tw-items-center tw-gap-1 tw-ml-0.5">
                        {visibleFilters.map((filter) => {
                            const isBulkSearch = filter.type === FILTER_TYPE.BULK_SEARCH;
                            const badgeVariant = FILTER_BADGE_VARIANT[filter.type] ?? 'info';
                            const closeIcon = CLOSE_ICON_BY_VARIANT[badgeVariant];

                            return (
                                <Badge
                                    key={filter.type}
                                    variant={badgeVariant}
                                    className={
                                        '!tw-h-auto tw-px-3 tw-py-1.5 !tw-text-[8px] !tw-font-bold '
                                        + '!tw-leading-none [&>span]:tw-flex [&>span]:tw-items-center '
                                        + '[&>span]:tw-leading-none'
                                    }
                                >
                                    <span className="tw-flex tw-items-center tw-gap-0.5 tw-leading-none">
                                        {isBulkSearch && bulkSearchLoading && (
                                            <Loader
                                                size="xs"
                                                className="!tw-m-0 !tw-h-3 !tw-w-3"
                                            />
                                        )}
                                        <span className="search-filter-tag tw-leading-none tw-flex tw-items-center">
                                            {formatFilterBadgeLabel(filter, t)}
                                        </span>
                                        {onRemoveFilter && (
                                            <button
                                                type="button"
                                                className={
                                                    'tw-inline-flex tw-items-center tw-justify-center '
                                                    + 'tw-border-0 tw-bg-transparent tw-p-0 tw-cursor-pointer'
                                                }
                                                aria-label={t('MONITOR.CLEAR_FILTERS')}
                                                onClick={() => onRemoveFilter(filter)}
                                            >
                                                <Icon
                                                    icon={XMarkIcon}
                                                    size="xs"
                                                    variant={closeIcon.variant}
                                                    colorScheme={closeIcon.colorScheme}
                                                />
                                            </button>
                                        )}
                                    </span>
                                </Badge>
                            );
                        })}
                        <Input
                            id="item-search-input"
                            type="text"
                            className={
                                'tw-flex-1 tw-min-w-[8rem] tw-bg-transparent tw-shadow-none '
                                + 'tw-outline-none tw-text-t-sm tw-text-t-primary !tw-border-none'
                            }
                            placeholder={t('SEARCH_PLACEHOLDER')}
                            value={inputValue}
                            onChange={(event) => setInputValue(event.target.value)}
                            aria-label={t('SEARCH_PLACEHOLDER')}
                        />
                    </div>
                </div>
            </div>

            {errorCode === ERROR_CODE_SEARCH_QUERY_INVALID && errorMessage && (
                <div className="tw-mt-1">
                    <span
                        id="search-validation-message"
                        className={VALIDATION_MESSAGE_CLASS[errorSeverity]}
                    >
                        {errorMessage}
                    </span>
                </div>
            )}

            {itemsMeta && itemsMeta.visibleRowCount > 0 && (
                <div className="tw-mt-1">
                    <FoundItemsLabel
                        visibleRowCount={itemsMeta.visibleRowCount}
                        numberFound={itemsMeta.numberFound}
                        maxNumberFound={itemsMeta.maxNumberFound}
                        countingItems={countingItems}
                        hasCursor={itemsMeta.hasCursor}
                        onRefreshCount={onRefreshCount}
                        countRecalculated={countRecalculated}
                    />
                </div>
            )}

            <FilterBuilder
                open={isFilterBuilderOpen}
                initialFilter={editingFilter}
                category={selectedCategory}
                onClose={() => {
                    setIsFilterBuilderOpen(false);
                    setEditingFilter(null);
                }}
                onSaved={handleFilterSaved}
                onQueryChange={handleFilterQueryChange}
            />
        </div>
    );
}
